import React, { useState, useEffect, useContext } from 'react';
import { Modal, View, Text, TextInput, Pressable, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import DataOps from '../../../data/DataOps';
import { GlobalStateContext } from '../../../components/general/GlobalStateProvider';

interface AddCoachModalProps {
    visible: boolean;
    onClose: () => void;
    onAddCoach: (coach: any) => void;
}

const AddCoachModal: React.FC<AddCoachModalProps> = ({
    visible,
    onClose,
    onAddCoach,
}) => {
    const { user } = useContext(GlobalStateContext);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedCoach, setSelectedCoach] = useState<any | null>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Debounced search
    useEffect(() => {
        const searchCoaches = async () => {
            if (searchQuery.trim().length < 2) {
                setSearchResults([]);
                setShowSuggestions(false);
                return;
            }

            setIsSearching(true);
            setShowSuggestions(true);

            try {
                const results = await DataOps.searchCoachesByEmail(searchQuery);
                
                // Add host as first option if user is a host and search matches their email
                const suggestions: any[] = [];
                
                if (user && user.accountType === 'host') {
                    const hostEmail = (user.email || '').toLowerCase();
                    const query = searchQuery.toLowerCase();
                    if (hostEmail.includes(query)) {
                        suggestions.push({
                            ...user,
                            isHost: true,
                            displayName: `${user.firstName} ${user.lastName} (You - Host)`,
                        });
                    }
                }

                // Add coach results
                results.forEach(coach => {
                    suggestions.push({
                        ...coach,
                        displayName: `${coach.firstName || ''} ${coach.lastName || ''}`.trim() || coach.email,
                    });
                });

                setSearchResults(suggestions);
            } catch (error) {
                console.error('Error searching coaches:', error);
            } finally {
                setIsSearching(false);
            }
        };

        const timeoutId = setTimeout(searchCoaches, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery, user]);

    const handleSelectCoach = (coach: any) => {
        setSelectedCoach(coach);
        setSearchQuery(coach.email || '');
        setShowSuggestions(false);
    };

    const handleAddCoach = async () => {
        if (!selectedCoach || !user) return;

        onAddCoach(selectedCoach);
        handleClose();
    };

    const handleClose = () => {
        setSearchQuery('');
        setSearchResults([]);
        setSelectedCoach(null);
        setShowSuggestions(false);
        onClose();
    };

    return (
        <Modal
            visible={visible}
            animationType="fade"
            transparent={true}
            onRequestClose={handleClose}
        >
            <View style={styles.modalOverlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.headerTitle}>Add Coach</Text>
                        <Pressable onPress={handleClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={24} color="#666" />
                        </Pressable>
                    </View>

                    {/* Content */}
                    <View style={styles.content}>
                        <Text style={styles.label}>Search by Email or Phone</Text>
                        <Text style={styles.subtitle}>
                            Search for registered coaches by their email or phone number. You can also add yourself as a coach.
                        </Text>

                        {/* Search Input */}
                        <View style={styles.searchContainer}>
                            <MaterialCommunityIcons name="magnify" size={20} color="#666" style={styles.searchIcon} />
                            <TextInput
                                style={styles.searchInput}
                                placeholder="Enter email or phone..."
                                value={searchQuery}
                                onChangeText={setSearchQuery}
                                onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                                autoCapitalize="none"
                            />
                            {isSearching && (
                                <ActivityIndicator size="small" color="#2196F3" style={styles.loadingIndicator} />
                            )}
                        </View>

                        {/* Suggestions Dropdown */}
                        {showSuggestions && searchResults.length > 0 && (
                            <View style={styles.suggestionsContainer}>
                                <ScrollView style={styles.suggestionsList} nestedScrollEnabled>
                                    {searchResults.map((coach, index) => (
                                        <Pressable
                                            key={coach.userId || index}
                                            style={[
                                                styles.suggestionItem,
                                                selectedCoach?.userId === coach.userId && styles.suggestionItemSelected
                                            ]}
                                            onPress={() => handleSelectCoach(coach)}
                                        >
                                            <View style={styles.suggestionContent}>
                                                <View style={styles.coachAvatar}>
                                                    <MaterialCommunityIcons
                                                        name={coach.isHost ? "home-city" : "whistle"}
                                                        size={24}
                                                        color={coach.isHost ? "#2196F3" : "#FF9800"}
                                                    />
                                                </View>
                                                <View style={styles.coachInfo}>
                                                    <Text style={styles.coachName}>{coach.displayName}</Text>
                                                    <Text style={styles.coachEmail}>{coach.email}</Text>
                                                    {coach.specialties && coach.specialties.length > 0 && (
                                                        <Text style={styles.coachSpecialties}>
                                                            {coach.specialties.join(', ')}
                                                        </Text>
                                                    )}
                                                </View>
                                            </View>
                                            {selectedCoach?.userId === coach.userId && (
                                                <MaterialCommunityIcons name="check-circle" size={20} color="#3C8248" />
                                            )}
                                        </Pressable>
                                    ))}
                                </ScrollView>
                            </View>
                        )}

                        {/* No Results */}
                        {showSuggestions && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
                            <View style={styles.noResults}>
                                <MaterialCommunityIcons name="account-search" size={48} color="#ccc" />
                                <Text style={styles.noResultsText}>No coaches found</Text>
                                <Text style={styles.noResultsSubtext}>
                                    {DataOps.isValidEmail(searchQuery) || DataOps.isValidPhone(searchQuery)
                                        ? 'This coach is not registered yet.'
                                        : 'Make sure the email or phone is correct.'}
                                </Text>
                                {(DataOps.isValidEmail(searchQuery) || DataOps.isValidPhone(searchQuery)) && (
                                    <Pressable
                                        style={styles.inviteButton}
                                        onPress={async () => {
                                            const hostName = user ? `${user.firstName} ${user.lastName}` : 'A host';
                                            const success = await DataOps.sendCoachInvitation(searchQuery, hostName);
                                            if (success) {
                                                alert(`Invitation sent to ${searchQuery}! They will receive a link to join SnapCamp as a coach.`);
                                                setSearchQuery('');
                                                setShowSuggestions(false);
                                            } else {
                                                alert('Failed to send invitation. Please try again.');
                                            }
                                        }}
                                    >
                                        <MaterialCommunityIcons name="email-send" size={20} color="#fff" />
                                        <Text style={styles.inviteButtonText}>Invite to join SnapCamp</Text>
                                    </Pressable>
                                )}
                            </View>
                        )}

                        {/* Selected Coach Preview */}
                        {selectedCoach && (
                            <View style={styles.selectedCoachPreview}>
                                <Text style={styles.previewLabel}>Selected Coach:</Text>
                                <View style={styles.previewCard}>
                                    <View style={styles.previewAvatar}>
                                        <MaterialCommunityIcons
                                            name={selectedCoach.isHost ? "home-city" : "whistle"}
                                            size={32}
                                            color={selectedCoach.isHost ? "#2196F3" : "#FF9800"}
                                        />
                                    </View>
                                    <View style={styles.previewInfo}>
                                        <Text style={styles.previewName}>{selectedCoach.displayName}</Text>
                                        <Text style={styles.previewEmail}>{selectedCoach.email}</Text>
                                        {selectedCoach.bio && (
                                            <Text style={styles.previewBio} numberOfLines={2}>{selectedCoach.bio}</Text>
                                        )}
                                    </View>
                                </View>
                            </View>
                        )}
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable style={[styles.cancelButton]} onPress={handleClose}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable
                            style={[styles.addButton, !selectedCoach && styles.addButtonDisabled]}
                            onPress={handleAddCoach}
                            disabled={!selectedCoach}
                        >
                            <MaterialCommunityIcons name="plus" size={20} color="#fff" />
                            <Text style={styles.addButtonText}>Add Coach</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: '#fff',
        borderRadius: 12,
        width: '100%',
        maxWidth: 600,
        maxHeight: '80%',
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 16,
        elevation: 10,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#E0E0E0',
    },
    headerTitle: {
        fontSize: 20,
        fontWeight: '600',
        color: '#222',
    },
    closeButton: {
        padding: 4,
    },
    content: {
        flex: 1,
        padding: 20,
    },
    label: {
        fontSize: 16,
        fontWeight: '600',
        color: '#222',
        marginBottom: 8,
    },
    subtitle: {
        fontSize: 14,
        color: '#666',
        marginBottom: 16,
        lineHeight: 20,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#ddd',
        borderRadius: 8,
        paddingHorizontal: 12,
        backgroundColor: '#F8F8F8',
        marginBottom: 16,
    },
    searchIcon: {
        marginRight: 8,
    },
    searchInput: {
        flex: 1,
        paddingVertical: 12,
        fontSize: 15,
        color: '#222',
        outlineStyle: 'none',
    } as any,
    loadingIndicator: {
        marginLeft: 8,
    },
    suggestionsContainer: {
        maxHeight: 250,
        borderWidth: 1,
        borderColor: '#ddd',
        borderRadius: 8,
        backgroundColor: '#fff',
        marginBottom: 16,
    },
    suggestionsList: {
        flex: 1,
    },
    suggestionItem: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    suggestionItemSelected: {
        backgroundColor: '#E3F2FD',
    },
    suggestionContent: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    coachAvatar: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: '#f0f0f0',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    },
    coachInfo: {
        flex: 1,
    },
    coachName: {
        fontSize: 15,
        fontWeight: '600',
        color: '#222',
        marginBottom: 2,
    },
    coachEmail: {
        fontSize: 13,
        color: '#666',
        marginBottom: 2,
    },
    coachSpecialties: {
        fontSize: 12,
        color: '#999',
        fontStyle: 'italic',
    },
    noResults: {
        alignItems: 'center',
        paddingVertical: 40,
    },
    noResultsText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#666',
        marginTop: 12,
        marginBottom: 4,
    },
    noResultsSubtext: {
        fontSize: 13,
        color: '#999',
        textAlign: 'center',
        marginBottom: 16,
    },
    inviteButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FF9800',
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 8,
        gap: 8,
        marginTop: 8,
    },
    inviteButtonText: {
        fontSize: 15,
        fontWeight: '600',
        color: '#fff',
    },
    selectedCoachPreview: {
        marginTop: 8,
    },
    previewLabel: {
        fontSize: 14,
        fontWeight: '600',
        color: '#222',
        marginBottom: 8,
    },
    previewCard: {
        flexDirection: 'row',
        padding: 16,
        backgroundColor: '#F8F8F8',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#ddd',
    },
    previewAvatar: {
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: '#fff',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 16,
    },
    previewInfo: {
        flex: 1,
    },
    previewName: {
        fontSize: 16,
        fontWeight: '600',
        color: '#222',
        marginBottom: 4,
    },
    previewEmail: {
        fontSize: 14,
        color: '#666',
        marginBottom: 4,
    },
    previewBio: {
        fontSize: 13,
        color: '#666',
        lineHeight: 18,
    },
    footer: {
        flexDirection: 'row',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderTopWidth: 1,
        borderTopColor: '#E0E0E0',
        gap: 12,
    },
    cancelButton: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#ddd',
        alignItems: 'center',
    },
    cancelButtonText: {
        fontSize: 15,
        fontWeight: '600',
        color: '#666',
    },
    addButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 12,
        borderRadius: 8,
        backgroundColor: '#3C8248',
        gap: 8,
    },
    addButtonDisabled: {
        backgroundColor: '#ccc',
    },
    addButtonText: {
        fontSize: 15,
        fontWeight: '600',
        color: '#fff',
    },
});

export default AddCoachModal;

